import { isArray, isArrayNotEmpty, objectEntries } from '.'

export type TraversedEntry<P, V> = readonly [parent: P, value: V]

export type TraverserCollectionContext<I, P, O> = Readonly<{
    input: I

    /** Previously mapped out children */
    children: readonly TraversedEntry<P, O>[]

    /** Previously traversed parents */
    parents: readonly P[]
}>

/**
 * Utility class that helps mapping out tree like structures into something else
 */
export abstract class Traverser<I, P, O> {
    /**
     * This function needs to yield from the current input, the relevant children
     * So the traverser can go down the tree structure
     */
    protected abstract mapChildEntries (input: I): readonly TraversedEntry<P, I>[]

    /**
     * After it went down, it needs now to collect the results back up
     */
    protected abstract collect (context: TraverserCollectionContext<I, P, O>): O

    traverse (input: I, parents: readonly P[] = []): O {
        const children: TraversedEntry<P, O>[] = []

        for (const [parent, child] of this.mapChildEntries(input)) {
            children.push([parent, this.traverse(child, [...parents, parent])])
        }

        return this.collect({ input, parents, children })
    }
}

/**
 * Utility class to traverse and replace plain objects
 */
export class PlainObjectTraverser<T> extends Traverser<T, string | number, T> {
    constructor (private readonly remap: <O>(obj: O, depth: readonly (string | number)[]) => O) {
        super()
    }

    protected override mapChildEntries (input: T): TraversedEntry<string | number, T>[] {
        if (isArray(input)) {
            return input.map((child, k) => [k, child]) as TraversedEntry<string | number, T>[]
        }

        if (input && typeof input === 'object') {
            return objectEntries(input) as TraversedEntry<string | number, T>[]
        }

        return []
    }

    protected override collect ({ input, parents, children }: TraverserCollectionContext<T, string | number, T>): T {
        if (!isArrayNotEmpty(children)) {
            /** Not an array or object */
            return this.remap(input, parents)
        }

        if (typeof children[0][0] === 'number') {
            /** Should always be an array */
            return this.remap(children.map(([, c]) => c) as T, parents)
        }

        return this.remap(Object.fromEntries(children) as T, parents)
    }
}
